import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { HttpService } from '@api/http.service'
import { CustomerAttribute } from '@api/models/CustomerInfoCall'

import {catchError, first, map, mergeMap, switchMap} from 'rxjs/operators'
import { OrderInfoActions } from './data.actions'
import { of } from 'rxjs'
import { CustomerInfoActions } from '../../account/user-profile/user-profile.actions'
import { NGXLogger } from 'ngx-logger'
import { OrderPaymentMethodActions } from '../states/payment-methods/payment-methods.actions'
import {complaintActions} from "../states/complaint/complaint.actions";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {SuccessSnackBarComponent} from "../../utils/components/success-snack-bar/success-snack-bar.component";
import {ErrorSnackBarComponent} from "../../utils/components/error-snack-bar/error-snack-bar.component";

const parseDate = (date: string): string => {
  if (date.trim() === '') return ''
  const [dd, MM, yyyy] = date.split(' ')[0].split('.')
  const dateObj = new Date(+yyyy, +MM, +dd)
  dateObj.setDate(dateObj.getDate() + 1)
  return `${dateObj.getFullYear()}.${dateObj.getMonth()}.${dateObj.getDate()}`
}

@Injectable()
export class OrderEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly httpService: HttpService,
    private readonly logger: NGXLogger,
    private readonly modals: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {}

  $c4LoginSuccess = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginSuccess),
      map(action => {
        return OrderInfoActions.orderInfoLoad({ payload: action.payload.uuid })
      }),
    )
  })

  $getAttrInfo = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderInfoActions.orderInfoLoad),
      switchMap(action =>
        this.httpService.getAttributesInfo(action.payload).pipe(
          first(),
          map((result: { parameter: CustomerAttribute[] }) =>
            OrderInfoActions.orderInfoSuccess({
              payload: result.parameter.map(value => ({
                attributeId: value.attribute_id,
                orderNumber: value.attribute_order_number,
                invoiceEndDate: parseDate(value.attribute_invoice_end_date),
                name: value.attribute_name,
                startDate: value.attribute_begin_date,
                endDate: value.attribute_end_date,
                description: value.attribute_description,
                recipient: {
                  firstName: value.attribute_recipient_firstname,
                  lastName: value.attribute_recipient_lastname,
                  company: value.attribute_recipient_company,
                },
                price: this.convertToCurrencyString(
                  value.attribute_next_debit_amount,
                  value.attribute_next_debit_currency,
                ),
                status: value.attribute_status,
                type: value.attribute_type,
                uuid: value.uuid_orderpos,
                paymentId: value.attribute_payment_id,
                downloadUrl: value.download_url,
                releases: value.releases,
                promotions: value.promotions,
              })),
            }),
          ),
          catchError(() => of(OrderInfoActions.orderInfoFail())),
        ),
      ),
    )
  })

  $c4LoginFailureOrders = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginFail),
      switchMap(action => {
        return of(OrderInfoActions.orderInfoFail())
      }),
    )
  })

  convertToCurrencyString(amount: string, unit: string): string {
    if (amount === '') amount = '0'

    let parsedAmount = parseFloat(amount)

    if (!isNaN(parsedAmount)) {
      if (unit === 'EUR') {
        return parsedAmount.toFixed(2) + '€'
      } else {
        return parsedAmount.toFixed(2) + ' ' + unit
      }
    } else {
      this.logger.warn(`${parsedAmount} is not a valid number`)
      return ''
    }
  }

  $trySavePaymentMethod = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderPaymentMethodActions.trySaveChanges),
      switchMap(action => {
        return this.httpService.savePaymentMethod(action.payload).pipe(
          switchMap(() => of(OrderPaymentMethodActions.saveChangesSuccess())),
          catchError(() => of(OrderPaymentMethodActions.saveChangesError())),
        )
      }),
    )
  })

  $trySaveComplaint = createEffect(() => {
    return this.actions$.pipe(
      ofType(complaintActions.trySaveComplaint),
      mergeMap(action => {
        return this.httpService.saveComplaint(action.payload.complaint, action.payload.order).pipe(
          switchMap(() => of(complaintActions.saveComplaintSuccess())),
          catchError((e) => of(complaintActions.saveComplaintError({payload: e})))
        )
      })
    )
  })

  $finishModalSaving = createEffect(() => {
    return this.actions$.pipe(
      ofType(complaintActions.saveComplaintSuccess),
      mergeMap(() => {
        this.modals.closeAll();
        this.snackBar.openFromComponent(SuccessSnackBarComponent, {
          panelClass: ['success-snack-bar'],
          data: {title: 'ORDERS.DIALOG.COMPLAINT.SNACKBAR_INFO'}
        })
        return of(complaintActions.saveComplaintFinished())
      })
    )
  })

  $finishModalSavingOnError = createEffect(() => {
    return this.actions$.pipe(
      ofType(complaintActions.saveComplaintError),
      mergeMap(() => {
        this.snackBar.openFromComponent(ErrorSnackBarComponent, {
          duration: 5000,
          panelClass: ['error-snack-bar'],
          data: {title: 'ORDERS.DIALOG.COMPLAINT.SNACKBAR_ERROR'}
        })
        return of(complaintActions.saveComplaintFinished())
      })
    )
  })
}
